import React from 'react';
import {
  Box,
  IconButton,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';

import { HOME_VIEW_ROLE_LABELS, HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import WidgetCard from '../common/WidgetCard';
import UpcomingAssignmentRow from './UpcomingAssignmentRow';

const STRINGS = {
  title: 'Upcoming assignments',
  empty: 'No upcoming assignments',
  back: 'Back',
};

type UpcomingAssignmentsPageProps = {
  onClose: () => void;
  assignments: HomeUpcomingAssignment[];
  viewRole: HomeViewRole;
  getTermLabel?: (spaceId: string) => string | null;
  onOpenAssignment?: (
    assignment: HomeUpcomingAssignment
  ) => void | Promise<void>;
};

const UpcomingAssignmentsPage = ({
  onClose,
  assignments,
  viewRole,
  getTermLabel,
  onOpenAssignment,
}: UpcomingAssignmentsPageProps) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: { xs: '100%', md: '800px' },
        margin: '0 auto',
        p: { xs: 2, md: 4 },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: '100%',
        bgcolor: palette.background.default,
        boxSizing: 'border-box',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        mb={3}
        onClick={onClose}
        sx={{
          cursor: 'pointer',
          width: 'fit-content',
          '&:hover': { opacity: 0.8 },
        }}
        role="button"
        tabIndex={0}
        aria-label="Go back to Home"
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClose();
          }
        }}
      >
        <IconButton
          size="medium"
          component="div"
          sx={{ p: 0, mr: 1, pointerEvents: 'none' }}
        >
          <Icons.ArrowBack
            sx={{ color: palette.primary.main }}
            fontSize="medium"
          />
        </IconButton>
        <Typography
          variant="body1"
          fontWeight="medium"
          color={palette.primary.main}
        >
          {STRINGS.back}
        </Typography>
      </Box>

      <Box
        sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <WidgetCard title={STRINGS.title} minHeight="100%" contentGap={0}>
          <Box sx={{ overflowY: 'auto', flex: 1, height: '100%', pr: 1 }}>
            {assignments.length ? (
              assignments.map((assignment, index) => (
                <UpcomingAssignmentRow
                  key={assignment.id}
                  assignment={assignment}
                  viewRole={viewRole}
                  termLabel={getTermLabel?.(assignment.spaceId) ?? null}
                  showDivider={index < assignments.length - 1}
                  onOpen={onOpenAssignment}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary" py={2}>
                {STRINGS.empty} in{' '}
                {HOME_VIEW_ROLE_LABELS[viewRole].toLowerCase()} view.
              </Typography>
            )}
          </Box>
        </WidgetCard>
      </Box>
    </Box>
  );
};

export default UpcomingAssignmentsPage;
